import React, { useState } from "react";
import {
  usePlayer,
  useGame,
  useRound,
  useStage,
} from "@empirica/core/player/classic/react";
import { getTaskConfig } from "../utils/taskConfig.js";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { recordSubmittedIntegrityText } from "../utils/integrityTracking.js";
import {
  PredictionButtonPage,
  formatPredictionValue,
} from "../components/PredictionButtonPage.jsx";
import {
  PeerRationalesPanel,
  useStaticSyntheticPeers,
} from "../components/PeerRationalesPanel.jsx";

// Second belief elicitation for post-peer conditions: read two peer rationales
// during the peer-review stage, then restate the belief before the reflection
// activity runs. Same response space as every other elicitation.
export function PostPeerBelief() {
  const player = usePlayer();
  const game = useGame();
  const round = useRound();
  const stage = useStage();
  const treatment = game?.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const peers = useStaticSyntheticPeers({ game, round, stage });

  const [prediction, setPrediction] = useState(() => {
    const existing = player.get("postPeerPrediction");
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const [confidence, setConfidence] = useState(() => {
    const existing = player.get("postPeerConfidence");
    return existing === undefined || existing === null ? "" : String(existing);
  });

  const priorPrediction =
    player.get("preLobbyPredictionBeforeAI") ??
    player.get("preLobbyPrediction") ??
    (game.get("playerBeliefs") || {})[player.id]?.initialBelief;
  const formattedPriorPrediction =
    formatPredictionValue(priorPrediction, taskConfig) ||
    String(priorPrediction ?? "");

  const formatBelief = (value) => formatPredictionValue(value, taskConfig);

  const handleSubmit = (choice, _rationale, submittedConfidence) => {
    const timestamp = Date.now();

    player.set("postPeerPrediction", choice);
    player.set("postPeerPredictionAt", timestamp);
    player.set("postPeerConfidence", submittedConfidence);
    player.set("postPeerConfidenceAt", timestamp);
    // Anchor for the reflection stage countdown.
    player.set("postPeerBeliefCompletedAt", timestamp);

    // Mirror into game.playerBeliefs, the same place the initial and final
    // beliefs live. The exit survey reads that copy first when it decides which
    // baseline the switch-reflection question is asked against; without the
    // mirror it silently falls back to the player scope, and if that read ever
    // came back empty the participant would be asked about the wrong baseline.
    //
    // This is a read-modify-write on a shared game attribute, so it only runs
    // once the server's game-start record has actually arrived. An unsynced read
    // would come back empty and the write would then replace the whole map,
    // dropping the initial beliefs and the synthetic peers' entries. Skipping is
    // safe: the player-scope keys above are written unconditionally and are what
    // the exit survey falls back to.
    try {
      const existingBeliefs = game.get("playerBeliefs");
      if (existingBeliefs && existingBeliefs[player.id]) {
        game.set("playerBeliefs", {
          ...existingBeliefs,
          [player.id]: {
            ...existingBeliefs[player.id],
            postPeerPrediction: choice,
            postPeerPredictionAt: timestamp,
            postPeerConfidence: submittedConfidence,
            postPeerConfidenceAt: timestamp,
          },
        });
      }
    } catch (_) {}

    recordSubmittedIntegrityText({
      playerId: player.id,
      fieldId: "postpeer-belief-prediction",
      text: String(choice ?? ""),
      phase: "in-game",
      stageId: stage?.id || "post-peer-belief",
      stageName: "post-peer-belief",
      detectedAt: timestamp,
    });

    savePlayerMeta({
      player,
      name: "postPeerBelief",
      value: {
        prediction: choice,
        confidence: submittedConfidence,
        timestamp,
      },
      debugTag: "post-peer belief",
    });

    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "click",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "postPeerPrediction",
        value: choice,
        confidence: submittedConfidence ?? null,
      });
      round.set("eventLog", events);
    } catch (_) {}

    player.stage.set("submittedAt", timestamp);
    player.stage.set("submit", true);
  };

  return (
    <PredictionButtonPage
      onPredictionSelect={setPrediction}
      onPredictionSubmit={handleSubmit}
      selectedPrediction={prediction}
      options={taskConfig.options}
      predictionInput={taskConfig.predictionInput}
      predictionDisplayFormatter={(value) => formatPredictionValue(value, taskConfig)}
      questionText={taskConfig.questionText}
      DetailsComponent={taskConfig.DetailsComponent}
      showLoanDetails={false}
      collapsibleDetails
      detailsToggle={taskConfig.detailsToggle}
      detailsTitle={taskConfig.detailsTitle}
      confidence={confidence}
      onConfidenceChange={setConfidence}
      requireConfidence={true}
      confidenceMin={taskConfig.confidenceMin}
      confidenceMax={taskConfig.confidenceMax}
      requireWholeNumberConfidence={taskConfig.requireWholeNumberConfidence}
      confidenceLabel={taskConfig.confidenceFieldLabel}
      confidencePlaceholder={taskConfig.confidencePlaceholder}
      confidenceHelpText={taskConfig.confidenceHelpText}
      confidenceErrorText={taskConfig.confidenceErrorText}
      integrityNotice={taskConfig.integrityNotice}
      integrityFieldPrefix="postpeer-belief"
      title="Update Your Estimate"
      description={
        formattedPriorPrediction
          ? `Your estimate was: ${formattedPriorPrediction}. Now that you have read the reasoning of two other participants, please give your estimate again (you may keep it the same or change it).`
          : "Now that you have read the reasoning of two other participants, please give your estimate again (you may keep it the same or change it)."
      }
      buttonText="Continue"
    >
      <PeerRationalesPanel
        peers={peers}
        formatBelief={formatBelief}
        subtitle="Reopen the two other participants' responses while updating your estimate."
      />
    </PredictionButtonPage>
  );
}
